import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import MultiSelectDropdown from '@/components/ui/MultiSelectDropdown';
import { 
  Users, Building2, Phone, RefreshCw, Plus, Edit, Trash2, 
  Bot, ChevronRight, ChevronDown, FolderTree, MessageSquare
} from 'lucide-react';
import { OrganizationalStructure as OrganizationalStructureType } from '@/types/bi';
import { AIPrompt, AIPromptType, AuditorInstruction } from '@/types/bi';
import biApi from '@/services/biApi';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { toast } from '@/components/ui/use-toast';
import InteractiveMenuBuilder from './InteractiveMenuBuilder';
import { useAuth } from '@/contexts/AuthContext';
import { Copy, CheckCircle2, Rocket } from 'lucide-react';
import apiService from '@/services/api';

interface OrganizationalStructureProps {
  clientId?: number;
}

interface Department {
  department_id: number;
  department_name: string;
  agents_count: number;
}

const OrganizationalStructureComponent: React.FC<OrganizationalStructureProps> = ({ clientId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState<OrganizationalStructureType[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<number>>(new Set());
  const [agentBotToken, setAgentBotToken] = useState('');
  const [isSavingToken, setIsSavingToken] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  // Estados para modais
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OrganizationalStructureType | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<AIPrompt | null>(null);
  const [editingDepartment, setEditingDepartment] = useState<OrganizationalStructureType | null>(null);
  
  // Estados para formulários
  const [agentForm, setAgentForm] = useState({
    agent_id: '',
    agent_name: '',
    department_id: '',
    contact_phone: '',
    hasMultipleDepartments: false,
    additionalDepartments: [] as string[],
  });
  
  const [departmentForm, setDepartmentForm] = useState({
    department_id: '',
    department_name: '',
  });
  
  const [promptForm, setPromptForm] = useState({
    prompt_type: 'ORCHESTRATOR' as AIPromptType,
    name: '',
    description: '',
    system_prompt: '',
    user_prompt_template: '',
    output_schema: '',
    auditor_instructions: [] as AuditorInstruction[],
    is_active: true,
  });

  const loadData = async () => {
    if (clientId === undefined) {
      setIsLoading(false);
      setData([]);
      setDepartments([]);
      return;
    }

    try {
      setIsLoading(true);
      const [structures, departmentsData, promptsData] = await Promise.all([
        biApi.getOrganizationalStructure(clientId),
        biApi.getDepartments(clientId),
        biApi.getPrompts({ clientId }),
      ]);
      setData(structures || []);
      setDepartments(departmentsData || []);
      setPrompts(promptsData || []);
      
      // Expandir todos os departamentos por padrão
      const deptIds = new Set(departmentsData.map((d: Department) => d.department_id));
      setExpandedDepartments(deptIds);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados',
        variant: 'destructive',
      });
      setData([]);
      setDepartments([]);
      setPrompts([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    if (clientId && isAdmin) {
      loadAgentBotToken();
    }
  }, [clientId, isAdmin]);

  const loadAgentBotToken = async () => {
    if (!clientId) return;
    try {
      const client = await apiService.get(`/clients/${clientId}`);
      setAgentBotToken(client.chatconnect_agent_bot_token || '');
    } catch (error) {
      console.error('Erro ao carregar token do AgentBot:', error);
    }
  };

  const handleSaveAgentBotToken = async () => {
    if (!clientId) return;
    try {
      setIsSavingToken(true);
      await apiService.put(`/clients/${clientId}`, {
        chatconnect_agent_bot_token: agentBotToken,
      });
      toast({
        title: 'Sucesso',
        description: 'Token do AgentBot salvo com sucesso',
      });
    } catch (error: any) {
      console.error('Erro ao salvar token:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível salvar o token',
        variant: 'destructive',
      });
    } finally {
      setIsSavingToken(false);
    }
  };

  const handleCopyUrl = () => {
    const url = 'https://hookworkflowrabbit.cleoia.com.br/webhook/ia_atomossolucao';
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
    toast({
      title: 'Copiado!',
      description: 'URL copiada para a área de transferência',
    });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadData();
  };

  const toggleDepartment = (departmentId: number) => {
    const newExpanded = new Set(expandedDepartments);
    if (newExpanded.has(departmentId)) {
      newExpanded.delete(departmentId);
    } else {
      newExpanded.add(departmentId);
    }
    setExpandedDepartments(newExpanded);
  };

  const handleCreateDepartment = () => {
    setEditingDepartment(null);
    setDepartmentForm({
      department_id: '',
      department_name: '',
    });
    setIsDepartmentModalOpen(true);
  };

  const handleEditDepartment = (dept: Department) => {
    // Buscar o registro placeholder (agent_id = 0) do departamento
    const deptRecord = data.find(
      item => item.department_id === dept.department_id && item.agent_id === 0
    );
    
    if (deptRecord) {
      setEditingDepartment(deptRecord);
      setDepartmentForm({
        department_id: dept.department_id.toString(),
        department_name: dept.department_name,
      });
      setIsDepartmentModalOpen(true);
    } else {
      toast({
        title: 'Erro',
        description: 'Registro do departamento não encontrado',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteDepartment = async (departmentId: number) => {
    if (!clientId) {
      toast({
        title: 'Erro',
        description: 'Cliente não selecionado',
        variant: 'destructive',
      });
      return;
    }

    // Buscar o departamento na lista de departamentos
    const department = departments.find(d => d.department_id === departmentId);
    if (!department) {
      toast({
        title: 'Erro',
        description: 'Departamento não encontrado',
        variant: 'destructive',
      });
      return;
    }

    // Verificar se há atendentes no departamento
    const agentsInDept = data.filter(item => item.department_id === departmentId && item.agent_id !== 0);
    if (agentsInDept.length > 0) {
      toast({
        title: 'Erro',
        description: 'Não é possível excluir um departamento que possui atendentes. Remova os atendentes primeiro.',
        variant: 'destructive',
      });
      return;
    }

    if (!confirm('Tem certeza que deseja excluir este departamento?')) return;

    try {
      console.log('🗑️ [OrganizationalStructure] Tentando excluir departamento:', {
        departmentId,
        departmentName: department.department_name,
        clientId
      });

      await biApi.deleteDepartment(departmentId, clientId);
      
      console.log('✅ [OrganizationalStructure] Departamento excluído com sucesso');
      
      toast({
        title: 'Sucesso',
        description: 'Departamento excluído com sucesso',
      });
      
      // Recarregar dados após exclusão
      await loadData();
    } catch (error: any) {
      console.error('❌ [OrganizationalStructure] Erro ao excluir departamento:', error);
      console.error('❌ [OrganizationalStructure] Detalhes do erro:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || error?.message || 'Não foi possível excluir o departamento',
        variant: 'destructive',
      });
    }
  };

  const handleSaveDepartment = async () => {
    if (!clientId) return;

    try {
      if (editingDepartment) {
        // Atualizar departamento existente usando endpoint específico
        await biApi.updateDepartment(editingDepartment.id, {
          client_id: clientId,
          department_name: departmentForm.department_name,
        });
        toast({
          title: 'Sucesso',
          description: 'Departamento atualizado com sucesso',
        });
      } else {
        // Criar novo departamento
        await biApi.createDepartment({
          client_id: clientId,
          department_name: departmentForm.department_name,
          department_id: parseInt(departmentForm.department_id),
        });
        toast({
          title: 'Sucesso',
          description: 'Departamento criado com sucesso',
        });
      }
      setIsDepartmentModalOpen(false);
      setEditingDepartment(null);
      loadData();
    } catch (error: any) {
      console.error('Erro ao salvar departamento:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível salvar o departamento',
        variant: 'destructive',
      });
    }
  };

  const handleCreateAgent = () => {
    if (departments.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Crie pelo menos um departamento antes de cadastrar atendentes',
        variant: 'destructive',
      });
      return;
    }
    setEditingItem(null);
    setAgentForm({
      agent_id: '',
      agent_name: '',
      department_id: '',
      contact_phone: '',
      hasMultipleDepartments: false,
      additionalDepartments: [],
    });
    setIsAgentModalOpen(true);
  };

  const handleEditAgent = (item: OrganizationalStructureType) => {
    setEditingItem(item);
    // Buscar todos os departamentos deste atendente
    const agentDepartments = safeData.filter(
      (a) => a.agent_id === item.agent_id && a.agent_id !== 0
    );
    const additionalDepts = agentDepartments
      .filter((a) => a.department_id !== item.department_id)
      .map((a) => a.department_id.toString());
    
    setAgentForm({
      agent_id: item.agent_id.toString(),
      agent_name: item.agent_name,
      department_id: item.department_id.toString(),
      contact_phone: item.contact_phone || '',
      hasMultipleDepartments: additionalDepts.length > 0,
      additionalDepartments: additionalDepts,
    });
    setIsAgentModalOpen(true);
    setIsAgentModalOpen(true);
  };

  const handleDeleteAgent = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir este atendente?')) return;

    try {
      await biApi.deleteOrganizationalStructure(id, clientId);
      toast({
        title: 'Sucesso',
        description: 'Atendente excluído com sucesso',
      });
      loadData();
    } catch (error) {
      console.error('Erro ao excluir atendente:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o atendente',
        variant: 'destructive',
      });
    }
  };

  const handleSaveAgent = async () => {
    if (!clientId) return;

    try {
      if (editingItem) {
        // Atualizar atendente existente
        await biApi.updateOrganizationalStructure(editingItem.id, {
          client_id: clientId,
          agent_name: agentForm.agent_name,
          department_id: parseInt(agentForm.department_id),
          contact_phone: agentForm.contact_phone || undefined,
        });

        // Se tem múltiplos departamentos, criar/atualizar registros adicionais
        if (agentForm.hasMultipleDepartments && agentForm.additionalDepartments.length > 0) {
          const agentId = parseInt(agentForm.agent_id);
          const mainDepartmentId = parseInt(agentForm.department_id);
          
          // Buscar departamentos existentes deste atendente
          const existingAgentDepts = safeData.filter(
            (a) => a.agent_id === agentId && a.agent_id !== 0
          );
          
          // Criar registros para departamentos adicionais que não existem
          for (const deptIdStr of agentForm.additionalDepartments) {
            const deptId = parseInt(deptIdStr);
            if (deptId === mainDepartmentId) continue; // Pular o departamento principal
            
            const exists = existingAgentDepts.some(a => a.department_id === deptId);
            if (!exists) {
              const dept = departments.find(d => d.department_id === deptId);
              if (dept) {
                await biApi.createOrganizationalStructure({
                  client_id: clientId,
                  agent_id: agentId,
                  agent_name: agentForm.agent_name,
                  department_id: deptId,
                  contact_phone: agentForm.contact_phone || undefined,
                });
              }
            } else {
              // Atualizar nome e telefone nos registros existentes
              const existingRecord = existingAgentDepts.find(a => a.department_id === deptId);
              if (existingRecord) {
                await biApi.updateOrganizationalStructure(existingRecord.id, {
                  client_id: clientId,
                  agent_name: agentForm.agent_name,
                  contact_phone: agentForm.contact_phone || undefined,
                });
              }
            }
          }
          
          // Remover departamentos que foram desmarcados (exceto o principal)
          const currentDeptIds = [
            mainDepartmentId,
            ...agentForm.additionalDepartments.map(d => parseInt(d))
          ];
          for (const existing of existingAgentDepts) {
            // Não deletar o registro principal nem os que ainda estão selecionados
            if (existing.id !== editingItem.id && !currentDeptIds.includes(existing.department_id)) {
              await biApi.deleteOrganizationalStructure(existing.id, clientId);
            }
          }
        } else {
          // Se desmarcou múltiplos departamentos, remover todos os adicionais
          const agentId = parseInt(agentForm.agent_id);
          const mainDepartmentId = parseInt(agentForm.department_id);
          const existingAgentDepts = safeData.filter(
            (a) => a.agent_id === agentId && a.agent_id !== 0
          );
          
          for (const existing of existingAgentDepts) {
            // Não deletar o registro principal
            if (existing.id !== editingItem.id && existing.department_id !== mainDepartmentId) {
              await biApi.deleteOrganizationalStructure(existing.id, clientId);
            }
          }
        }

        toast({
          title: 'Sucesso',
          description: 'Atendente atualizado com sucesso',
        });
      } else {
        // Criar novo atendente
        const agentId = parseInt(agentForm.agent_id);
        
        // Criar registro principal
        await biApi.createOrganizationalStructure({
          client_id: clientId,
          agent_id: agentId,
          agent_name: agentForm.agent_name,
          department_id: parseInt(agentForm.department_id),
          contact_phone: agentForm.contact_phone || undefined,
        });

        // Se tem múltiplos departamentos, criar registros adicionais
        if (agentForm.hasMultipleDepartments && agentForm.additionalDepartments.length > 0) {
          const mainDepartmentId = parseInt(agentForm.department_id);
          
          for (const deptIdStr of agentForm.additionalDepartments) {
            const deptId = parseInt(deptIdStr);
            if (deptId === mainDepartmentId) continue; // Pular o departamento principal
            
            const dept = departments.find(d => d.department_id === deptId);
            if (dept) {
              await biApi.createOrganizationalStructure({
                client_id: clientId,
                agent_id: agentId,
                agent_name: agentForm.agent_name,
                department_id: deptId,
                contact_phone: agentForm.contact_phone || undefined,
              });
            }
          }
        }

        toast({
          title: 'Sucesso',
          description: `Atendente criado com sucesso${agentForm.hasMultipleDepartments && agentForm.additionalDepartments.length > 0 ? ` em ${agentForm.additionalDepartments.length + 1} departamento(s)` : ''}`,
        });
      }
      setIsAgentModalOpen(false);
      loadData();
    } catch (error: any) {
      console.error('Erro ao salvar atendente:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível salvar o atendente',
        variant: 'destructive',
      });
    }
  };

  const handleCreatePrompt = () => {
    setEditingPrompt(null);
    setPromptForm({
      prompt_type: 'ORCHESTRATOR',
      name: '',
      description: '',
      system_prompt: '',
      user_prompt_template: '',
      output_schema: '',
      auditor_instructions: [],
      is_active: true,
    });
    setIsPromptModalOpen(true);
  };

  const handleAddAuditorInstruction = () => {
    setPromptForm({
      ...promptForm,
      auditor_instructions: [
        ...promptForm.auditor_instructions,
        {
          entrada: '',
          tipo_atendimento: 'atendimento_ia',
          department_id: null,
          department_name: null,
        },
      ],
    });
  };

  const handleRemoveAuditorInstruction = (index: number) => {
    setPromptForm({
      ...promptForm,
      auditor_instructions: promptForm.auditor_instructions.filter((_, i) => i !== index),
    });
  };

  const handleUpdateAuditorInstruction = (
    index: number,
    field: keyof AuditorInstruction,
    value: any
  ) => {
    const updated = [...promptForm.auditor_instructions];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    // Se mudou para atendimento_ia, remover department_id e department_name
    if (field === 'tipo_atendimento' && value === 'atendimento_ia') {
      updated[index].department_id = null;
      updated[index].department_name = null;
    }
    // Se mudou department_id, atualizar também o department_name
    // Exceto quando department_id === 0 (caso especial de "resolvido")
    if (field === 'department_id' && value !== 0) {
      const selectedDept = departments.find(
        (d) => d.department_id === value
      );
      updated[index].department_name = selectedDept
        ? selectedDept.department_name
        : null;
    }
    // Se department_id === 0, manter department_name como "resolvido" se já estiver definido
    if (field === 'department_id' && value === 0) {
      updated[index].department_name = 'resolvido';
    }
    setPromptForm({
      ...promptForm,
      auditor_instructions: updated,
    });
  };

  const handleEditPrompt = (prompt: AIPrompt) => {
    setEditingPrompt(prompt);
    setPromptForm({
      prompt_type: prompt.prompt_type,
      name: prompt.name,
      description: prompt.description || '',
      system_prompt: prompt.system_prompt,
      user_prompt_template: prompt.user_prompt_template,
      output_schema: JSON.stringify(prompt.output_schema || {}, null, 2),
      auditor_instructions: prompt.auditor_instructions || [],
      is_active: prompt.is_active,
    });
    setIsPromptModalOpen(true);
  };

  const [promptToDelete, setPromptToDelete] = useState<AIPrompt | null>(null);
  const [isDeletePromptDialogOpen, setIsDeletePromptDialogOpen] = useState(false);

  const handleDeletePrompt = async (prompt: AIPrompt) => {
    setPromptToDelete(prompt);
    setIsDeletePromptDialogOpen(true);
  };

  const confirmDeletePrompt = async () => {
    if (!promptToDelete) return;

    try {
      await biApi.deletePrompt(promptToDelete.id);
      toast({
        title: 'Sucesso',
        description: 'Prompt deletado com sucesso',
      });
      setIsDeletePromptDialogOpen(false);
      setPromptToDelete(null);
      loadData();
    } catch (error: any) {
      console.error('Erro ao deletar prompt:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || error?.message || 'Não foi possível deletar o prompt',
        variant: 'destructive',
      });
    }
  };

  const handleSavePrompt = async () => {
    if (!clientId) return;

    try {
      let outputSchema;
      try {
        outputSchema = promptForm.output_schema ? JSON.parse(promptForm.output_schema) : null;
      } catch (e) {
        toast({
          title: 'Erro',
          description: 'JSON do output_schema inválido',
          variant: 'destructive',
        });
        return;
      }

      // Validar instruções do auditor se for tipo AUDITOR
      if (promptForm.prompt_type === 'AUDITOR') {
        const hasEmptyEntries = promptForm.auditor_instructions.some(
          (inst) => !inst.entrada || inst.entrada.trim() === ''
        );
        if (hasEmptyEntries) {
          toast({
            title: 'Erro',
            description: 'Todas as instruções devem ter uma entrada preenchida',
            variant: 'destructive',
          });
          return;
        }
        const hasInvalidDepartments = promptForm.auditor_instructions.some(
          (inst) =>
            inst.tipo_atendimento === 'atendimento_humano' &&
            (!inst.department_id || inst.department_id === null)
        );
        if (hasInvalidDepartments) {
          toast({
            title: 'Erro',
            description: 'Instruções de atendimento humano devem ter um departamento selecionado',
            variant: 'destructive',
          });
          return;
        }
      }

      if (editingPrompt) {
        await biApi.updatePrompt(editingPrompt.id, {
          name: promptForm.name,
          description: promptForm.description || undefined,
          system_prompt: promptForm.system_prompt,
          user_prompt_template: promptForm.user_prompt_template,
          output_schema: outputSchema,
          auditor_instructions:
            promptForm.prompt_type === 'AUDITOR'
              ? promptForm.auditor_instructions
              : undefined,
          is_active: promptForm.is_active,
        });
        toast({
          title: 'Sucesso',
          description: 'Prompt atualizado com sucesso',
        });
      } else {
        await biApi.createPrompt({
          client_id: clientId,
          prompt_type: promptForm.prompt_type,
          name: promptForm.name,
          description: promptForm.description || undefined,
          system_prompt: promptForm.system_prompt,
          user_prompt_template: promptForm.user_prompt_template,
          output_schema: outputSchema,
          auditor_instructions:
            promptForm.prompt_type === 'AUDITOR'
              ? promptForm.auditor_instructions
              : undefined,
          is_active: promptForm.is_active,
        });
        toast({
          title: 'Sucesso',
          description: 'Prompt criado com sucesso',
        });
      }
      setIsPromptModalOpen(false);
      loadData();
    } catch (error: any) {
      console.error('Erro ao salvar prompt:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível salvar o prompt',
        variant: 'destructive',
      });
    }
  };

  if (!clientId) {
    return (
      <Card className="border-0 shadow-lg animate-in fade-in slide-in-from-top-4">
        <CardContent className="p-12 text-center">
          <Building2 className="h-12 w-12 text-muted mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">Selecione um cliente para visualizar a estrutura organizacional</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return <AtomicLoadingScreen />;
  }

  // Garantir que data seja sempre um array
  const safeData = Array.isArray(data) ? data : [];
  
  // Filtrar apenas atendentes (agent_id !== 0)
  const agents = safeData.filter(item => item.agent_id !== 0);
  
  // Agrupar atendentes por departamento
  const agentsByDepartment = agents.reduce((acc, agent) => {
    const deptId = agent.department_id;
    if (!acc[deptId]) {
      acc[deptId] = [];
    }
    acc[deptId].push(agent);
    return acc;
  }, {} as Record<number, OrganizationalStructureType[]>);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
                Configuração de Estrutura
              </CardTitle>
              <CardDescription>Gerencie departamentos, atendentes e prompts de IA</CardDescription>
            </div>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              variant="outline"
              size="icon"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="tree" className="w-full">
            <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-6' : 'grid-cols-5'}`}>
              <TabsTrigger value="tree" className="flex items-center gap-2">
                <FolderTree className="h-4 w-4" />
                Árvore
              </TabsTrigger>
              <TabsTrigger value="departments" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Departamentos
              </TabsTrigger>
              <TabsTrigger value="agents" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Atendentes
              </TabsTrigger>
              <TabsTrigger value="prompts" className="flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Prompts
              </TabsTrigger>
              <TabsTrigger value="menus" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Menus Interativos
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="go-live" className="flex items-center gap-2">
                  <Rocket className="h-4 w-4" />
                  Go-Live
                </TabsTrigger>
              )}
            </TabsList>

            {/* Tab: Árvore Genealógica */}
            <TabsContent value="tree" className="mt-6">
              {departments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FolderTree className="h-16 w-16 mx-auto mb-4 text-muted" />
                  <p className="text-lg font-medium mb-2">Nenhuma estrutura cadastrada</p>
                  <p className="text-sm mb-4">Comece criando departamentos</p>
                  <Button onClick={handleCreateDepartment} className="bg-primary hover:bg-primary-600">
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Departamento
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {departments.map((dept) => {
                    const agentsInDept = agentsByDepartment[dept.department_id] || [];
                    const isExpanded = expandedDepartments.has(dept.department_id);
                    
                    return (
                      <Card key={dept.department_id} className="border-l-4 border-l-primary-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <button
                                onClick={() => toggleDepartment(dept.department_id)}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-primary-600" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </button>
                              <Building2 className="h-5 w-5 text-primary-600" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-lg">{dept.department_name}</span>
                                  <Badge variant="secondary" className="bg-primary/10 text-primary-700">
                                    {agentsInDept.length} atendente{agentsInDept.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground">ID: {dept.department_id}</div>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditDepartment(dept)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteDepartment(dept.department_id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="mt-4 ml-8 space-y-2 border-l-2 border-l-primary-200 pl-4">
                              {agentsInDept.length === 0 ? (
                                <div className="text-sm text-muted-foreground py-2">
                                  Nenhum atendente neste departamento
                                </div>
                              ) : (
                                agentsInDept.map((agent) => (
                                  <Card key={agent.id} className="bg-muted/50 border-l-2 border-l-secondary-500">
                                    <CardContent className="p-3">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 flex-1">
                                          <Users className="h-4 w-4 text-secondary-600" />
                                          <div>
                                            <div className="font-medium">{agent.agent_name}</div>
                                            <div className="text-xs text-muted-foreground">
                                              ID: {agent.agent_id}
                                              {agent.contact_phone && ` • ${agent.contact_phone}`}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleEditAgent(agent)}
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteAgent(agent.id)}
                                          >
                                            <Trash2 className="h-3 w-3 text-destructive" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setAgentForm({
                                    agent_id: '',
                                    agent_name: '',
                                    department_id: dept.department_id.toString(),
                                    contact_phone: '',
                                  });
                                  setIsAgentModalOpen(true);
                                }}
                                className="w-full mt-2 border-primary/20 text-primary hover:bg-primary/10"
                              >
                                <Plus className="h-3 w-3 mr-2" />
                                Adicionar Atendente
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Tab: Departamentos */}
            <TabsContent value="departments" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Gerenciar Departamentos</h3>
                <Button onClick={handleCreateDepartment} className="bg-primary hover:bg-primary-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Departamento
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {departments.map((dept) => {
                  const agentsInDept = agentsByDepartment[dept.department_id] || [];
                  return (
                    <Card key={dept.department_id} className="hover:shadow-md transition-shadow border-l-4 border-l-primary-500">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Building2 className="h-4 w-4 text-primary-600" />
                              <span className="font-medium">{dept.department_name}</span>
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <div><strong>ID:</strong> {dept.department_id}</div>
                              <div><strong>Atendentes:</strong> {agentsInDept.length}</div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditDepartment(dept)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteDepartment(dept.department_id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {departments.length === 0 && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    <Building2 className="h-16 w-16 mx-auto mb-4 text-muted" />
                    <p className="text-lg font-medium mb-2">Nenhum departamento cadastrado</p>
                    <Button onClick={handleCreateDepartment} variant="outline" className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Primeiro Departamento
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab: Atendentes */}
            <TabsContent value="agents" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Gerenciar Atendentes</h3>
                <Button onClick={handleCreateAgent} className="bg-primary hover:bg-primary-600" disabled={departments.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Atendente
                </Button>
              </div>
              {departments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-16 w-16 mx-auto mb-4 text-muted" />
                  <p className="text-lg font-medium mb-2">Crie departamentos primeiro</p>
                  <p className="text-sm mb-4">Você precisa criar pelo menos um departamento antes de cadastrar atendentes</p>
                  <Button onClick={handleCreateDepartment} variant="outline" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Departamento
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {agents.map((agent) => (
                    <Card key={agent.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Users className="h-4 w-4 text-secondary-600" />
                              <span className="font-medium">{agent.agent_name}</span>
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <div><strong>ID:</strong> {agent.agent_id}</div>
                              <div><strong>Departamento:</strong> {agent.department_name}</div>
                              {agent.contact_phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  <span>{agent.contact_phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditAgent(agent)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteAgent(agent.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {agents.length === 0 && (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                      <Users className="h-16 w-16 mx-auto mb-4 text-muted" />
                      <p className="text-lg font-medium mb-2">Nenhum atendente cadastrado</p>
                      <Button onClick={handleCreateAgent} variant="outline" className="mt-4">
                        <Plus className="h-4 w-4 mr-2" />
                        Criar Primeiro Atendente
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Tab: Menus Interativos */}
            <TabsContent value="menus" className="mt-6">
              <InteractiveMenuBuilder clientId={clientId} />
            </TabsContent>

            {/* Tab: Go-Live (Apenas Admin) */}
            {isAdmin && (
              <TabsContent value="go-live" className="mt-6">
                <Card className="border-l-4 border-l-primary-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Rocket className="h-5 w-5 text-primary-600" />
                      Ativação da IA para Atendimento
                    </CardTitle>
                    <CardDescription>
                      Instruções para ativar a IA após a configuração completa
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Instruções */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Passo a Passo para Ativação</h3>
                        
                        {/* Etapa 0: Configuração de Label "aberto" */}
                        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-l-4 border-purple-500">
                          <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                            <span className="text-lg">📌</span> Etapa 0: Criar Label "aberto"
                          </h4>
                          <p className="text-sm text-gray-700 mb-3">
                            Antes de configurar as automações, é necessário criar uma label chamada <strong>"aberto"</strong> no ChatConnect.
                          </p>
                          
                          <div className="bg-white rounded-lg p-3 mb-3 border border-purple-200">
                            <p className="text-xs font-mono bg-gray-100 px-2 py-1 rounded inline-block mb-2">
                              Nome da Label: <strong className="text-primary-600">aberto</strong>
                            </p>
                            <p className="text-xs text-gray-600">
                              💡 Copie o nome acima para facilitar o cadastro
                            </p>
                          </div>

                          <div className="mt-4 mb-4">
                            <img 
                              src="https://miniotypes3.cleoia.com.br/clients/rotulo_aberto.png" 
                              alt="Configuração da label 'aberto' no ChatConnect" 
                              className="w-full rounded-lg border-2 border-purple-200 shadow-md"
                            />
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-purple-200 mt-4">
                            <p className="text-sm text-gray-700">
                              Configure a label conforme a imagem acima. Esta label será usada para identificar conversas que estão abertas e aguardando atendimento.
                            </p>
                          </div>
                        </div>

                        {/* Etapa 1: Automação resolveConversa */}
                        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-l-4 border-green-500">
                          <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                            <span className="text-lg">🔄</span> Etapa 1: Configurar Automação "resolveConversa"
                          </h4>
                          
                          <div className="bg-white rounded-lg p-4 border border-green-200 mb-4">
                            <h5 className="font-semibold text-green-900 mb-2">Objetivo:</h5>
                            <p className="text-sm text-gray-700">
                              Limpar o agente, time e rótulos que estão atribuídos para destravar a conversa nos próximos contatos e também fechar o ticket aberto que está em andamento.
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-3 mb-3 border border-green-200">
                            <p className="text-xs font-mono bg-gray-100 px-2 py-1 rounded inline-block mb-2">
                              Nome da Automação: <strong className="text-primary-600">resolveConversa</strong>
                            </p>
                            <p className="text-xs text-gray-600 mb-2">
                              💡 Copie o nome acima para facilitar o cadastro
                            </p>
                            <div className="mt-2">
                              <Label className="text-xs text-gray-600">Webhook para esta automação:</Label>
                              <div className="flex gap-2 mt-1">
                                <Input
                                  value="https://hookworkflowrabbit.cleoia.com.br/webhook/resolved_conversation"
                                  readOnly
                                  className="flex-1 font-mono text-xs h-8"
                                />
                                <Button
                                  onClick={() => {
                                    navigator.clipboard.writeText('https://hookworkflowrabbit.cleoia.com.br/webhook/resolved_conversation');
                                    toast({
                                      title: 'Copiado!',
                                      description: 'URL do webhook copiada para a área de transferência',
                                    });
                                  }}
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 mb-4">
                            <img 
                              src="https://miniotypes3.cleoia.com.br/clients/resolved.png" 
                              alt="Configuração da automação resolveConversa no ChatConnect" 
                              className="w-full rounded-lg border-2 border-green-200 shadow-md"
                            />
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-green-200 mt-4">
                            <h5 className="font-semibold text-green-900 mb-2">Explicação por Seção:</h5>
                            <div className="space-y-3 text-sm text-gray-700">
                              <div>
                                <p className="font-medium mb-1">📋 Nome da Regra: resolveConversa</p>
                                <p className="text-gray-600 text-xs">
                                  Nome identificador da automação. Use exatamente "resolveConversa" para manter consistência.
                                </p>
                              </div>
                              <div>
                                <p className="font-medium mb-1">⚡ Evento: Conversation Updated</p>
                                <p className="text-gray-600 text-xs">
                                  A automação será acionada sempre que uma conversa for atualizada, permitindo detectar mudanças de status.
                                </p>
                              </div>
                              <div>
                                <p className="font-medium mb-1">🔍 Condições: Status = Resolvido</p>
                                <p className="text-gray-600 text-xs">
                                  A automação só será executada quando o status da conversa for alterado para "Resolvido", garantindo que apenas conversas finalizadas sejam processadas.
                                </p>
                              </div>
                              <div>
                                <p className="font-medium mb-1">🎯 Ações Configuradas:</p>
                                <ul className="list-disc list-inside ml-2 space-y-1 text-xs text-gray-600">
                                  <li><strong>Assign to agent:</strong> None - Remove atribuição de agente</li>
                                  <li><strong>Assign a team:</strong> None - Remove atribuição de time</li>
                                  <li><strong>Remove a label:</strong> "aberto" - Remove a label de conversa aberta</li>
                                  <li><strong>Send Webhook Event:</strong> Envia evento para o webhook configurado</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Etapa 2: Configuração da IA */}
                        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-primary-50 rounded-lg border-l-4 border-primary-500">
                          <h4 className="font-semibold text-primary-900 mb-3 flex items-center gap-2">
                            <span className="text-lg">🤖</span> Etapa 2: Configuração da IA para Atendimento
                          </h4>
                          <p className="text-sm text-gray-700 mb-3">
                            Após configurar a label e a automação de resolução, configure a IA para atendimento automático.
                          </p>
                        </div>

                        <ol className="space-y-4 list-decimal list-inside ml-4">
                          <li className="space-y-2">
                            <p className="font-medium">Cadastrar o AgentBot na conta através do SuperAdmin:</p>
                            <ul className="list-disc list-inside ml-6 space-y-1 text-sm text-muted-foreground">
                              <li>Acesse: <strong>SuperAdmin → AgentBot → New Agent Bot</strong></li>
                              <li>Inserir o nome (que será o nome que o cliente escolheu como assistente)</li>
                              <li>Vincular a Account</li>
                              <li>Clicar em <strong>Create AgentBot</strong></li>
                              <li className="font-medium text-primary-600 mt-2 list-none">
                                ⚠️ <strong>IMPORTANTE:</strong> Após criar o AgentBot, copie o token gerado e cole no campo "Token do AgentBot" abaixo
                              </li>
                            </ul>
                          </li>
                          <li className="space-y-2">
                            <p className="font-medium">Ativar o AgentBot na conta:</p>
                            <ul className="list-disc list-inside ml-6 space-y-1 text-sm text-muted-foreground">
                              <li>Ainda em SuperAdmin, vá na sessão <strong>Accounts</strong></li>
                              <li>Escolha a empresa</li>
                              <li>Clique em <strong>Edit</strong> e marque <strong>Agent Bots</strong> caso esteja desmarcado</li>
                            </ul>
                          </li>
                          <li className="space-y-2">
                            <p className="font-medium">Configurar o AgentBot na conta do cliente:</p>
                            <ul className="list-disc list-inside ml-6 space-y-1 text-sm text-muted-foreground">
                              <li>Volte agora no acesso admin da conta do cliente</li>
                              <li>Acesse: <strong>Configurações → Caixa de Entrada</strong> (clique na configuração)</li>
                              <li>Vá em <strong>Configurações do Bot</strong></li>
                              <li>Escolha o AgentBot cadastrado</li>
                            </ul>
                          </li>
                        </ol>
                      </div>

                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                        <p className="text-sm text-yellow-800">
                          <strong>Observação:</strong> A partir do cadastro do AgentBot, as conversas sempre iniciarão como <strong>PENDENTE</strong>.
                        </p>
                      </div>
                    </div>

                    {/* Token do AgentBot */}
                    <div className="space-y-4 border-t pt-6">
                      <div>
                        <Label htmlFor="agent-bot-token">Token do AgentBot *</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                            id="agent-bot-token"
                            value={agentBotToken}
                            onChange={(e) => setAgentBotToken(e.target.value)}
                            placeholder="Cole o token do AgentBot aqui"
                            className="flex-1"
                          />
                          <Button
                            onClick={handleSaveAgentBotToken}
                            disabled={isSavingToken || !agentBotToken.trim()}
                          >
                            {isSavingToken ? 'Salvando...' : 'Salvar Token'}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Este token será salvo na coluna <code className="bg-muted px-1 rounded">chatconnect_agent_bot_token</code> da tabela clients
                        </p>
                      </div>
                    </div>

                    {/* URL do Webhook */}
                    <div className="space-y-4 border-t pt-6">
                      <div>
                        <Label>URL do Webhook para Configuração no ChatConnect</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                            value="https://hookworkflowrabbit.cleoia.com.br/webhook/ia_atomossolucao"
                            readOnly
                            className="flex-1 font-mono text-sm"
                          />
                          <Button
                            onClick={handleCopyUrl}
                            variant="outline"
                            size="icon"
                          >
                            {copiedUrl ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Copie esta URL e configure na aba <strong>Automação</strong> do ChatConnect
                        </p>
                      </div>

                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                        <p className="text-sm text-blue-800 font-medium mb-3">Como configurar no ChatConnect:</p>
                        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700 mb-4">
                          <li>Crie uma nova automação na aba <strong>Automação</strong> do ChatConnect</li>
                          <li>Configure conforme a imagem de exemplo abaixo</li>
                          <li>Cole a URL do webhook acima</li>
                        </ol>
                        
                        {/* Imagem de Exemplo */}
                        <div className="mt-4 mb-4">
                          <img 
                            src="https://miniotypes3.cleoia.com.br/clients/config_ia.png" 
                            alt="Exemplo de configuração de automação no ChatConnect" 
                            className="w-full rounded-lg border-2 border-blue-200 shadow-md"
                          />
                        </div>

                        {/* Explicações Detalhadas */}
                        <div className="mt-6 space-y-4">
                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                              <span className="text-primary-600">📋</span> Nome da Regra e Descrição
                            </h4>
                            <p className="text-sm text-gray-700">
                              Defina um nome descritivo para identificar facilmente esta automação e uma descrição opcional explicando seu propósito.
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                              <span className="text-primary-600">⚡</span> Evento
                            </h4>
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>Quando o webhook será acionado:</strong> Selecione <strong>"Message Created"</strong> para que o sistema envie um evento para cada mensagem criada no ChatConnect.
                            </p>
                            <p className="text-xs text-gray-600 italic">
                              Isso garante que todas as mensagens recebidas sejam processadas pela IA em tempo real.
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                              <span className="text-primary-600">🔍</span> Condições
                            </h4>
                            <div className="space-y-3 text-sm text-gray-700">
                              <div>
                                <p className="font-medium mb-1">1. Message Type = Incoming Message</p>
                                <p className="text-gray-600">
                                  Filtra apenas as mensagens recebidas (incoming), evitando processar mensagens enviadas pelo próprio sistema. Isso reduz significativamente a sobrecarga no webhook e garante que apenas mensagens dos clientes sejam processadas pela IA.
                                </p>
                              </div>
                              <div>
                                <p className="font-medium mb-1">2. Inbox</p>
                                <p className="text-gray-600 mb-1">
                                  Selecione a(s) caixa(s) de entrada que deseja monitorar. Você pode selecionar uma ou múltiplas inboxes.
                                </p>
                                <p className="text-xs text-primary-600 font-medium italic">
                                  💡 Observação: A mesma IA atenderá em todas as caixas selecionadas. Isso permite centralizar o atendimento mesmo com múltiplas inboxes.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                              <span className="text-primary-600">🎯</span> Ações
                            </h4>
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>Enviar Webhook Event:</strong> Configure para enviar os eventos para o webhook copiado acima (<code className="bg-gray-100 px-1 rounded text-xs">https://hookworkflowrabbit.cleoia.com.br/webhook/ia_atomossolucao</code>).
                            </p>
                            <p className="text-xs text-gray-600 italic">
                              Esta ação garante que todos os eventos de mensagens recebidas sejam enviados para o sistema de IA, permitindo que ela processe e responda automaticamente.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Observações Finais */}
                    <div className="space-y-4 border-t pt-6">
                      <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
                        <p className="text-sm text-orange-800 font-medium mb-2">⚠️ Observações Importantes:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-orange-700">
                          <li>Assim que a automação for cadastrada ela já estará <strong>ATIVA</strong> e começará a interação com os contatos</li>
                          <li>Alinhe o melhor horário como <strong>fora de pico</strong> para validar o fluxo</li>
                          <li>Para pausar a interação da IA, desative na sessão <strong>Automação</strong> do ChatConnect</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Tab: Prompts */}
            <TabsContent value="prompts" className="mt-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">Gerenciar Prompts de IA</h3>
                  <p className="text-sm text-muted-foreground">Configure e gerencie os prompts de inteligência artificial</p>
                </div>
                <Button 
                  onClick={handleCreatePrompt} 
                  className="bg-primary hover:bg-primary-600 text-white shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Prompt
                </Button>
              </div>
              
              {prompts.length === 0 ? (
                <Card className="border-2 border-dashed border-muted-foreground/20">
                  <CardContent className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="rounded-full bg-primary/10 p-4 mb-4">
                      <Bot className="h-12 w-12 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-foreground mb-2">Nenhum prompt configurado</h4>
                    <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                      Comece criando seu primeiro prompt de IA para personalizar o comportamento do sistema
                    </p>
                    <Button 
                      onClick={handleCreatePrompt} 
                      variant="outline" 
                      className="border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/40"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Primeiro Prompt
                    </Button>
                  </CardContent>
                </Card>
              ) : (
              <div className="grid grid-cols-1 gap-4">
                {prompts.map((prompt) => (
                    <Card 
                      key={prompt.id} 
                      className="group hover:shadow-lg transition-all duration-200 border-l-4 border-l-primary-500 bg-white"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Header com ícone e título */}
                            <div className="flex items-start gap-3 mb-3">
                              <div className="rounded-lg bg-primary/10 p-2 group-hover:bg-primary/20 transition-colors">
                                <Bot className="h-5 w-5 text-primary-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary-600 transition-colors">
                                  {prompt.name}
                                </h4>
                                {prompt.description && (
                                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                    {prompt.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Badges e informações */}
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <Badge 
                                variant={prompt.is_active ? 'default' : 'secondary'} 
                                className={prompt.is_active 
                                  ? 'bg-primary text-white border-primary shadow-sm' 
                                  : 'bg-muted text-muted-foreground border-muted-foreground/20'
                                }
                              >
                                {prompt.is_active ? 'Ativo' : 'Inativo'}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className="border-primary/30 text-primary-700 bg-primary/5 font-medium"
                              >
                                {prompt.prompt_type}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className="border-muted-foreground/20 text-muted-foreground bg-muted/50"
                              >
                                v{prompt.version}
                              </Badge>
                            </div>

                            {/* Data de criação */}
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>Criado em:</span>
                              <span className="font-medium">
                                {new Date(prompt.created_at).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>

                          {/* Botões de ação */}
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditPrompt(prompt)}
                              className="h-9 w-9 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                              title="Editar prompt"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeletePrompt(prompt)}
                              className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Excluir prompt"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                ))}
              </div>
                )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal: Criar/Editar Departamento */}
      <Dialog open={isDepartmentModalOpen} onOpenChange={(open) => {
        setIsDepartmentModalOpen(open);
        if (!open) {
          setEditingDepartment(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDepartment ? 'Editar Departamento' : 'Criar Departamento'}</DialogTitle>
            <DialogDescription>
              {editingDepartment ? 'Atualize os dados do departamento' : 'Preencha os dados do novo departamento'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="dept_id">ID do Departamento *</Label>
              <Input
                id="dept_id"
                value={departmentForm.department_id}
                onChange={(e) => setDepartmentForm({ ...departmentForm, department_id: e.target.value })}
                placeholder="Ex: 1"
                disabled={!!editingDepartment}
                required
              />
            </div>
            <div>
              <Label htmlFor="dept_name">Nome do Departamento *</Label>
              <Input
                id="dept_name"
                value={departmentForm.department_name}
                onChange={(e) => setDepartmentForm({ ...departmentForm, department_name: e.target.value })}
                placeholder="Ex: Financeiro"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsDepartmentModalOpen(false);
              setEditingDepartment(null);
            }}>
              Cancelar
            </Button>
            <Button onClick={handleSaveDepartment} className="bg-primary hover:bg-primary-600">
              {editingDepartment ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Criar/Editar Atendente */}
      <Dialog open={isAgentModalOpen} onOpenChange={setIsAgentModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Atendente' : 'Novo Atendente'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Atualize os dados do atendente' : 'Preencha os dados do novo atendente'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="agent_id">ID do Atendente *</Label>
                <Input
                  id="agent_id"
                  value={agentForm.agent_id}
                  onChange={(e) => setAgentForm({ ...agentForm, agent_id: e.target.value })}
                  placeholder="Ex: 1"
                  disabled={!!editingItem}
                  required
                />
              </div>
              <div>
                <Label htmlFor="agent_name">Nome do Atendente *</Label>
                <Input
                  id="agent_name"
                  value={agentForm.agent_name}
                  onChange={(e) => setAgentForm({ ...agentForm, agent_name: e.target.value })}
                  placeholder="Ex: João Silva"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="department_id">Departamento *</Label>
              <Select
                value={agentForm.department_id}
                onValueChange={(value) => {
                  // Se mudou o departamento principal, remover da lista de adicionais se estiver lá
                  const updatedAdditional = agentForm.additionalDepartments.filter(
                    d => d !== value
                  );
                  setAgentForm({ 
                    ...agentForm, 
                    department_id: value,
                    additionalDepartments: updatedAdditional
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um departamento" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.department_id} value={dept.department_id.toString()}>
                      {dept.department_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Opção para múltiplos departamentos */}
            <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex flex-col gap-1">
                <Label htmlFor="hasMultipleDepartments" className="text-sm font-medium cursor-pointer">
                  Faz parte de múltiplos departamentos
                </Label>
                <p className="text-xs text-muted-foreground">
                  Se marcado, você poderá vincular este atendente a outros departamentos
                </p>
              </div>
              <Switch
                id="hasMultipleDepartments"
                checked={agentForm.hasMultipleDepartments}
                onCheckedChange={(checked) => {
                  setAgentForm({ 
                    ...agentForm, 
                    hasMultipleDepartments: checked,
                    additionalDepartments: checked ? agentForm.additionalDepartments : []
                  });
                }}
              />
            </div>

            {/* Dropdown para departamentos adicionais */}
            {agentForm.hasMultipleDepartments && (
              <div>
                <Label htmlFor="additionalDepartments">Departamentos Adicionais</Label>
                <MultiSelectDropdown
                  options={departments
                    .filter(dept => dept.department_id.toString() !== agentForm.department_id)
                    .map(dept => ({
                      value: dept.department_id.toString(),
                      label: dept.department_name
                    }))}
                  value={agentForm.additionalDepartments}
                  onChange={(values) => {
                    setAgentForm({ 
                      ...agentForm, 
                      additionalDepartments: values 
                    });
                  }}
                  placeholder="Selecione os departamentos adicionais"
                  className="mt-2"
                />
                {agentForm.additionalDepartments.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {agentForm.additionalDepartments.length} departamento(s) adicional(is) selecionado(s)
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="contact_phone">Telefone de Contato</Label>
              <Input
                id="contact_phone"
                value={agentForm.contact_phone}
                onChange={(e) => setAgentForm({ ...agentForm, contact_phone: e.target.value })}
                placeholder="Ex: (11) 99999-9999"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAgentModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAgent} className="bg-primary hover:bg-primary-600">
              {editingItem ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Criar/Editar Prompt */}
      <Dialog open={isPromptModalOpen} onOpenChange={setIsPromptModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPrompt ? 'Editar Prompt' : 'Novo Prompt'}</DialogTitle>
            <DialogDescription>
              {editingPrompt ? 'Atualize o prompt de IA' : 'Configure um novo prompt de IA'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prompt_type">Tipo de Prompt *</Label>
                <Select
                  value={promptForm.prompt_type}
                  onValueChange={(value: AIPromptType) => {
                    const newForm = { ...promptForm, prompt_type: value };
                    // Limpar campos específicos ao mudar o tipo
                    if (value === 'AUDITOR') {
                      newForm.system_prompt = '';
                      newForm.user_prompt_template = '';
                      newForm.output_schema = '';
                      newForm.auditor_instructions = [];
                    } else {
                      newForm.auditor_instructions = [];
                    }
                    setPromptForm(newForm);
                  }}
                  disabled={!!editingPrompt}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ORCHESTRATOR">Orquestrador (Router & Classifier)</SelectItem>
                    <SelectItem value="AUDITOR">Auditor (Quality Assurance)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Nome do Prompt *</Label>
                <Input
                  id="name"
                  value={promptForm.name}
                  onChange={(e) => setPromptForm({ ...promptForm, name: e.target.value })}
                  placeholder="Ex: Orquestrador Principal"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={promptForm.description}
                onChange={(e) => setPromptForm({ ...promptForm, description: e.target.value })}
                placeholder="Breve descrição do prompt"
              />
            </div>

            {/* Interface específica para AUDITOR */}
            {promptForm.prompt_type === 'AUDITOR' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Instruções do Auditor *</Label>
                  <Button
                    type="button"
                    onClick={handleAddAuditorInstruction}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Instrução
                  </Button>
                </div>
                {promptForm.auditor_instructions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <p className="text-sm">Nenhuma instrução cadastrada</p>
                    <p className="text-xs mt-1">Clique em "Adicionar Instrução" para começar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {promptForm.auditor_instructions.map((instruction, index) => (
                      <Card key={index} className="p-4 border-l-4 border-l-primary-500">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-muted-foreground">
                              Instrução #{index + 1}
                            </span>
                            <Button
                              type="button"
                              onClick={() => handleRemoveAuditorInstruction(index)}
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div>
                            <Label htmlFor={`entrada-${index}`}>Entrada *</Label>
                            <Textarea
                              id={`entrada-${index}`}
                              value={instruction.entrada}
                              onChange={(e) =>
                                handleUpdateAuditorInstruction(index, 'entrada', e.target.value)
                              }
                              placeholder="Ex: O boleto foi gerado com sucesso! Posso te ajudar em algo mais?"
                              rows={2}
                              required
                            />
                          </div>
                          <div className={instruction.tipo_atendimento === 'atendimento_humano' ? 'grid grid-cols-2 gap-4' : ''}>
                            <div>
                              <Label htmlFor={`tipo-${index}`}>Tipo de Atendimento *</Label>
                              <Select
                                value={instruction.tipo_atendimento}
                                onValueChange={(value: 'atendimento_ia' | 'atendimento_humano') =>
                                  handleUpdateAuditorInstruction(index, 'tipo_atendimento', value)
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="atendimento_ia">Atendimento IA</SelectItem>
                                  <SelectItem value="atendimento_humano">
                                    Atendimento Humano
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {instruction.tipo_atendimento === 'atendimento_humano' && (
                              <div>
                                <Label htmlFor={`department-${index}`}>Departamento *</Label>
                                <Select
                                  value={
                                    instruction.department_id
                                      ? instruction.department_id.toString()
                                      : ''
                                  }
                                  onValueChange={(value) =>
                                    handleUpdateAuditorInstruction(
                                      index,
                                      'department_id',
                                      parseInt(value)
                                    )
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione um departamento" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {departments.map((dept) => (
                                      <SelectItem
                                        key={dept.department_id}
                                        value={dept.department_id.toString()}
                                      >
                                        {dept.department_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {instruction.tipo_atendimento === 'atendimento_ia' && (
                              <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20 mt-4">
                                <div className="flex flex-col gap-1">
                                  <Label htmlFor={`resolved-${index}`} className="text-sm font-medium">
                                    Conversa Resolvida
                                  </Label>
                                  <p className="text-xs text-muted-foreground">
                                    Se marcado, grava o departamento como "resolvido" (ID: 0)
                                  </p>
                                </div>
                                <Switch
                                  id={`resolved-${index}`}
                                  checked={instruction.department_id === 0 && instruction.department_name === 'resolvido'}
                                  onCheckedChange={(checked) => {
                                    const updated = [...promptForm.auditor_instructions];
                                    if (checked) {
                                      updated[index] = {
                                        ...updated[index],
                                        department_id: 0,
                                        department_name: 'resolvido',
                                      };
                                    } else {
                                      updated[index] = {
                                        ...updated[index],
                                        department_id: null,
                                        department_name: null,
                                      };
                                    }
                                    setPromptForm({
                                      ...promptForm,
                                      auditor_instructions: updated,
                                    });
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="system_prompt">System Prompt *</Label>
                  <Textarea
                    id="system_prompt"
                    value={promptForm.system_prompt}
                    onChange={(e) =>
                      setPromptForm({ ...promptForm, system_prompt: e.target.value })
                    }
                    placeholder="Digite o prompt do sistema..."
                    rows={6}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="user_prompt_template">User Prompt Template *</Label>
                  <Textarea
                    id="user_prompt_template"
                    value={promptForm.user_prompt_template}
                    onChange={(e) =>
                      setPromptForm({ ...promptForm, user_prompt_template: e.target.value })
                    }
                    placeholder="Digite o template do prompt do usuário..."
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="output_schema">Output Schema (JSON)</Label>
                  <Textarea
                    id="output_schema"
                    value={promptForm.output_schema}
                    onChange={(e) =>
                      setPromptForm({ ...promptForm, output_schema: e.target.value })
                    }
                    placeholder='{"intent": "string", "department": "string", ...}'
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Formato JSON válido</p>
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={promptForm.is_active}
                onChange={(e) => setPromptForm({ ...promptForm, is_active: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active">Prompt ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPromptModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePrompt} className="bg-primary hover:bg-primary-600">
              {editingPrompt ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: Confirmar exclusão de Prompt */}
      <AlertDialog open={isDeletePromptDialogOpen} onOpenChange={setIsDeletePromptDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o prompt "{promptToDelete?.name}"?
              <br />
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPromptToDelete(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePrompt}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrganizationalStructureComponent;
